import { createFileRoute, Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Section } from "design-system/molecules/Section"
import { Plus } from "lucide-react"
import {
  KnowledgeBaseItems,
  KnowledgeBaseItemsRef,
} from "modules/knowledge/components/KnowledgeBaseItems"
import { useCallback, useRef } from "react"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useTranslation } from "react-i18next"
import { BackofficeType, KnowledgeBaseItemKind } from "schema"
import z from "zod"

export const Route = createFileRoute("/_base/_auth/knowledge/knowledge-base")({
  component: Component,
  validateSearch: z.object({
    section: z
      .nativeEnum(KnowledgeBaseItemKind)
      .catch(KnowledgeBaseItemKind.QuestionAndAnswer)
      .default(KnowledgeBaseItemKind.QuestionAndAnswer),
    highlightId: z.coerce.string().optional(),
  }),
})

function Component() {
  const { t } = useTranslation()
  const { section, highlightId } = Route.useSearch()
  const { developerMode } = useDeveloperMode()
  const { data: employee } = useAuthEmployeeQuery()
  const knowledgeBaseItemsRef = useRef<KnowledgeBaseItemsRef | null>(null)

  const handleRef = useCallback((ref: KnowledgeBaseItemsRef | null) => {
    knowledgeBaseItemsRef.current = ref
  }, [])

  const handleAddClick = useCallback(() => {
    knowledgeBaseItemsRef.current?.addItemToActiveTab()
  }, [])

  return (
    <Section
      title={t("knowledge.knowledgeBase.title")}
      description={t("knowledge.knowledgeBase.description")}
      action={
        <div className="flex flex-wrap gap-2">
          {developerMode && employee?.backofficeType === BackofficeType.Superadmin && (
            <Button variant="outline" asChild>
              <Link to="/knowledge/suggestions">{t("knowledge.suggestions.title")}</Link>
            </Button>
          )}
          <Button type="button" onClick={handleAddClick}>
            <Plus className="h-4 w-4" />
            <span>{t("knowledge.form.addEntry")}</span>
          </Button>
        </div>
      }
    >
      <KnowledgeBaseItems
        key={section}
        ref={handleRef}
        highlightId={highlightId}
        section={section}
      />
    </Section>
  )
}
